import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,

} from '@mui/material';
import { supabase } from '../supabaseClient';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSecteur } from '../contexts/SecteurContext';
import { ActivityProvider } from '../contexts/ActivityContext';
import { useActivities } from '../contexts/ActivityContext';
import { useAuth } from '../contexts/AuthContext';  // Ajouter cet import en haut du fichier
import HoursInput from '../components/HoursInput';



function TimeEntry() {
  const { user, roles } = useAuth();  // Mettre à jour pour inclure roles
  const { selectedSecteur } = useSecteur();
  const { activities } = useActivities();

  // Log des activités
  useEffect(() => {
    
  }, [activities]);

  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd', { locale: fr }),
    activityId: '',
    hours: '',
    userId: '',
    releveCodeId: '',
    description: '',
    on_report: false,
  });

  // Filtrer les activités actives et liées au secteur
  const activeActivities = activities.filter(activity =>
    activity.is_active &&
    (!activity.sector_id || activity.sector_id === selectedSecteur)
  );

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [releveCodes, setReleveCodes] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    // Réinitialiser le formulaire et les utilisateurs quand le secteur change
    setFormData(prevData => ({
      ...prevData,
      userId: '',
      activityId: '',
      releveCodeId: '',
      description: '',
      hours: ''
    }));

    // Filtrer les utilisateurs pour le secteur sélectionné
    if (selectedSecteur) {
      fetchUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSecteur]); // On désactive l'avertissement car fetchUsers dépend de selectedSecteur

  const fetchUsers = async () => {
    try {
      if (!selectedSecteur) {
        console.warn('Aucun secteur sélectionné');
        setUsers([]);
        setFilteredUsers([]);
        return;
      }

      // Récupérer les utilisateurs avec leurs mandats actifs dans le secteur
      const { data, error } = await supabase
        .from('user_mandates')
        .select(`
          users (
            id, 
            full_name
          ),
          id,
          sector_id,
          is_active
        `)
        .eq('sector_id', selectedSecteur)
        .eq('is_active', true);

      if (error) throw error;

      const usersWithActiveSectorMandates = data?.map(mandate => ({
        ...mandate.users,
        mandate_id: mandate.id
      })).filter((user, index, self) =>
        index === self.findIndex((u) => u.id === user.id)
      ) || [];

      setUsers(usersWithActiveSectorMandates);
      setFilteredUsers(usersWithActiveSectorMandates);
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      showSnackbar('Erreur de chargement des utilisateurs', 'error');
    }
  };

  const fetchUserMandates = async (userId) => {
    try {
      if (!selectedSecteur || !userId) {
        console.warn('Secteur ou utilisateur non sélectionné');
        setReleveCodes([]);
        return null;
      }

      // Récupérer les codes relève associés aux mandats
      const { data: mandateData, error: mandateError } = await supabase
        .from('user_mandates')
        .select(`
          id,
          releve_codes (
            id,
            code,
            role,
            color,
            mandate_description,
            hours_per_month,
            monthly_limit,
            particularity,
            include_in_releve_rate
          ),
          sectors!inner (
            id
          )
        `)
        .eq('user_id', userId)
        .eq('sectors.id', selectedSecteur)
        .eq('is_active', true);

      if (mandateError) throw mandateError;

      // Récupérer les codes relève without_mandate
      const { data: withoutMandateCodes, error: withoutMandateError } = await supabase
        .from('releve_codes')
        .select('*')
        .eq('without_mandate', true);

      if (withoutMandateError) throw withoutMandateError;

      // Combiner les codes relève des mandats avec ceux without_mandate
      const mandateCodes = mandateData
        ?.filter(mandate => mandate.releve_codes)
        .map(mandate => mandate.releve_codes) || [];

      const uniqueReleveCodes = [...mandateCodes, ...withoutMandateCodes]
        .filter((code, index, self) =>
          index === self.findIndex((c) => c.id === code.id)
        );

      setReleveCodes(uniqueReleveCodes || []);
      return mandateData;
    } catch (error) {
      console.error('Erreur lors de la récupération des mandats utilisateur:', error);
      showSnackbar('Erreur de chargement des mandats', 'error');
      setReleveCodes([]);
      return null;
    }
  };

  const handleUserChange = async (event) => {
    const userId = event.target.value;

    // Mettre à jour le formulaire avec le nouvel utilisateur
    setFormData(prevData => ({
      ...prevData,
      userId: userId,
      releveCodeId: '',  // Réinitialiser le code relève
    }));

    // Récupérer les codes relève pour l'utilisateur
    if (userId) {
      await fetchUserMandates(userId);
    } else {
      setReleveCodes([]);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      if (!selectedSecteur) {
        showSnackbar('Veuillez sélectionner un secteur', 'error');
        return;
      }

      // Récupérer le mandat actif de l'utilisateur dans le secteur
      const { data: userMandates, error: mandateError } = await supabase
        .from('user_mandates')
        .select('id')
        .eq('user_id', formData.userId)
        .eq('sector_id', selectedSecteur)
        .eq('is_active', true);

      if (mandateError) throw mandateError;

      if (!userMandates || userMandates.length === 0) {
        showSnackbar('Aucun mandat actif trouvé pour cet utilisateur dans ce secteur', 'error');
        return;
      }

      // Prendre le premier mandat actif
      const userMandateId = userMandates[0].id;

      const pointageData = {
        date: formData.date,
        activity: formData.activityId,
        hours: parseFloat(formData.hours),
        user_mandate_id: userMandateId,
        releve_code_id: formData.releveCodeId || null,
        description: formData.description,
        on_report: formData.on_report,
      };

      const { error } = await supabase
        .from('pointage')
        .insert(pointageData);

      if (error) throw error;

      showSnackbar('Pointage enregistré avec succès');

      // Réinitialiser le formulaire
      setFormData({
        date: format(new Date(), 'yyyy-MM-dd', { locale: fr }),
        activityId: '',
        hours: '',
        userId: '',
        releveCodeId: '',
        description: '',
        on_report: false,
      });

      // Réinitialiser les codes relève
      setReleveCodes([]);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du pointage:', error);
      showSnackbar('Erreur lors de l\'enregistrement du pointage', 'error');
    }
  };




  useEffect(() => {
    // Lorsque la liste des utilisateurs est mise à jour
    if (users.length > 0 && user) {
      // Rechercher l'utilisateur connecté dans la liste
      const connectedUser = users.find(u => u.id === user.id);
      if (connectedUser) {
        // Mettre à jour le formulaire avec l'utilisateur connecté
        setFormData(prevData => ({
          ...prevData,
          userId: connectedUser.id
        }));
        // Charger les mandats pour l'utilisateur
        fetchUserMandates(connectedUser.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, user]); // On désactive l'avertissement car fetchUserMandates dépend de selectedSecteur

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Saisie des pointages
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Utilisateur</InputLabel>
                <Select
                  name="userId"
                  value={formData.userId}
                  onChange={handleUserChange}
                  label="Utilisateur"
                  disabled={!roles?.can_view_all_users}
                >
                  {filteredUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* affichage des heures */}

            <Grid item xs={12} sm={6}>
              <HoursInput
                value={parseFloat(formData.hours) || 0}
                onChange={(newValue) => setFormData(prev => ({ ...prev, hours: newValue }))}
                fullWidth
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.on_report}
                    onChange={(event) => {
                      setFormData(prevData => ({
                        ...prevData,
                        on_report: event.target.checked
                      }));
                    }}
                    name="on_report"
                    color="primary"
                  />
                }
                label="Fait Marquant"
                sx={{ mt: 2 }}
              />
            </Grid>


            <Grid item xs={12} sm={6}>

              <FormControl fullWidth>
                <InputLabel>Code relève</InputLabel>
                <Select
                  name="releveCodeId"
                  value={formData.releveCodeId}
                  onChange={handleChange}
                  label="Code relève"
                >
                  {releveCodes.map((code) => (
                    <MenuItem key={code.id} value={code.id}>
                      {code.code} - {code.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Activité</InputLabel>
                <Select
                  name="activityId"
                  value={formData.activityId}
                  onChange={handleChange}
                  label="Activité"
                >
                  {activeActivities.map((activity) => (
                    <MenuItem key={activity.id} value={activity.id}>
                      {activity.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>






            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>



            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Enregistrer le pointage
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const TimeEntryWithProvider = () => {
  return (
    <ActivityProvider>
      <TimeEntry />
    </ActivityProvider>
  );
}

export default TimeEntryWithProvider;
